(() => {
  const gallery = document.querySelector('media-gallery[data-relin-universal="true"]');
  if (!gallery || gallery.dataset.relinGalleryReady === 'true') return;

  const productInfo = gallery.closest('product-info');
  const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
  const viewerList = viewer?.querySelector('[id^="Slider-Gallery-"]');
  const thumbnailComponent = gallery.querySelector('[id^="GalleryThumbnails-"]');
  const thumbnailStrip = thumbnailComponent?.querySelector('[id^="Slider-Thumbnails-"]');
  const mediaModal = document.querySelector('product-modal[data-relin-universal-modal="true"]');
  if (!viewer || !viewerList || !thumbnailComponent || !thumbnailStrip || !mediaModal) return;

  window.__relinUniversalGalleryCleanup?.();
  gallery.dataset.relinGalleryReady = 'true';

  const listenerController = new AbortController();
  const timeoutIds = new Set();
  let thumbnailObserver;
  let modalObserver;

  const schedule = (callback, delay) => {
    const timeoutId = window.setTimeout(() => {
      timeoutIds.delete(timeoutId);
      callback();
    }, delay);
    timeoutIds.add(timeoutId);
    return timeoutId;
  };

  const cleanup = () => {
    listenerController.abort();
    thumbnailObserver?.disconnect();
    modalObserver?.disconnect();
    timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    timeoutIds.clear();
    if (window.__relinUniversalGalleryCleanup === cleanup) delete window.__relinUniversalGalleryCleanup;
  };

  window.__relinUniversalGalleryCleanup = cleanup;

  const configElement = document.querySelector('script[data-relin-universal-config]');
  let universalConfig = {};
  try {
    universalConfig = JSON.parse(configElement?.textContent || '{}');
  } catch (error) {
    universalConfig = {};
  }
  const colourGroups = {};
  const colourGroupOrder = {};
  const canonicalHeroPosition = {};
  const mediaIdToGroup = {};
  Object.entries(universalConfig.gallery || {}).forEach(([colour, config]) => {
    colourGroups[colour] = config.group;
    colourGroupOrder[config.group] = (config.positions || []).map(String);
    canonicalHeroPosition[config.group] = String(config.hero);
    (config.media_ids || []).forEach((mediaId) => {
      mediaIdToGroup[String(mediaId)] = config.group;
    });
  });

  let selectedTarget =
    thumbnailStrip.querySelector('[data-target] button[aria-current="true"]')?.closest('[data-target]')?.dataset.target ||
    thumbnailStrip.querySelector('[data-target]')?.dataset.target ||
    '';
  let activeColourGroup = '';

  const isFinePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;
  let lastDesktopMode = isDesktop();
  let allViewerItems = [];
  let allThumbnailItems = [];
  const viewerItemsByGroup = new Map();
  const thumbnailItemsByGroup = new Map();
  let lastPointerType = '';
  const previousButton = thumbnailComponent.querySelector('button[name="previous"]');
  const nextButton = thumbnailComponent.querySelector('button[name="next"]');

  const getItem = (target) =>
    Array.from(thumbnailStrip.querySelectorAll('[data-target]')).find((item) => item.dataset.target === target);

  const getSlide = (target) =>
    Array.from(viewerList.querySelectorAll('[data-media-id]')).find((item) => item.dataset.mediaId === target);

  const getOrderedItems = (items, group) => {
    const order = colourGroupOrder[group] || [];
    return [...items].sort((first, second) => {
      const firstIndex = order.indexOf(first.dataset.relinMediaPosition);
      const secondIndex = order.indexOf(second.dataset.relinMediaPosition);
      return (firstIndex === -1 ? Number.MAX_SAFE_INTEGER : firstIndex) -
        (secondIndex === -1 ? Number.MAX_SAFE_INTEGER : secondIndex);
    });
  };

  const captureMediaItems = () => {
    allViewerItems = Array.from(viewerList.children).filter((item) => item.matches('[data-media-id]'));
    allThumbnailItems = Array.from(thumbnailStrip.children).filter((item) => item.matches('[data-target]'));

    allViewerItems.forEach((item) => {
      const group = item.dataset.relinColourGroup || mediaIdToGroup[item.dataset.mediaId?.split('-').pop()] || '';
      if (group) item.dataset.relinColourGroup = group;
      if (!viewerItemsByGroup.has(group)) viewerItemsByGroup.set(group, []);
      viewerItemsByGroup.get(group).push(item);
    });
    allThumbnailItems.forEach((item) => {
      const targetId = item.dataset.target?.split('-').pop();
      const group = item.dataset.relinColourGroup || mediaIdToGroup[targetId] || '';
      if (group) item.dataset.relinColourGroup = group;
      if (!thumbnailItemsByGroup.has(group)) thumbnailItemsByGroup.set(group, []);
      thumbnailItemsByGroup.get(group).push(item);
    });
  };

  const resetSliderGeometry = () => {
    viewer.resetPages?.();
    thumbnailComponent.resetPages?.();
    viewerList.scrollTo({ left: 0, behavior: 'auto' });
    thumbnailStrip.scrollTo({ left: 0, top: 0, behavior: 'auto' });
  };

  const syncMobileSliderItems = (group) => {
    const activeViewerItems = getOrderedItems(viewerItemsByGroup.get(group) || [], group);
    const activeThumbnailItems = getOrderedItems(thumbnailItemsByGroup.get(group) || [], group);

    allViewerItems.forEach((item) => {
      item.remove();
      item.hidden = true;
      item.setAttribute('aria-hidden', 'true');
    });
    allThumbnailItems.forEach((item) => {
      item.remove();
      item.hidden = true;
      item.setAttribute('aria-hidden', 'true');
    });

    activeViewerItems.forEach((item) => {
      item.hidden = false;
      item.setAttribute('aria-hidden', 'false');
      viewerList.append(item);
    });
    activeThumbnailItems.forEach((item) => {
      item.hidden = false;
      item.setAttribute('aria-hidden', 'false');
      thumbnailStrip.append(item);
    });

    resetSliderGeometry();
  };

  const restoreDesktopSliderItems = () => {
    allViewerItems.forEach((item) => {
      item.hidden = false;
      item.setAttribute('aria-hidden', 'false');
      viewerList.append(item);
    });
    allThumbnailItems.forEach((item) => {
      item.hidden = false;
      item.setAttribute('aria-hidden', 'false');
      thumbnailStrip.append(item);
    });
    resetSliderGeometry();
  };

  const scrollSelectedIntoView = (target) => {
    const item = getItem(target);
    if (!item) return;

    if (isDesktop()) {
      const top = item.offsetTop - Math.max(0, (thumbnailStrip.clientHeight - item.offsetHeight) / 2);
      thumbnailStrip.scrollTo({ top, behavior: 'smooth' });
    } else {
      const left = item.offsetLeft - Math.max(0, (thumbnailStrip.clientWidth - item.offsetWidth) / 2);
      thumbnailStrip.scrollTo({ left, behavior: 'smooth' });
    }
  };

  const updateArrowState = () => {
    const maxScroll = isDesktop()
      ? Math.max(0, thumbnailStrip.scrollHeight - thumbnailStrip.clientHeight)
      : Math.max(0, thumbnailStrip.scrollWidth - thumbnailStrip.clientWidth);
    const currentScroll = isDesktop() ? thumbnailStrip.scrollTop : thumbnailStrip.scrollLeft;
    const hasOverflow = maxScroll > 1;
    if (previousButton) previousButton.disabled = !hasOverflow || currentScroll <= 1;
    if (nextButton) nextButton.disabled = !hasOverflow || currentScroll >= maxScroll - 1;
  };

  const syncRailHeight = () => {
    if (!isDesktop()) {
      thumbnailComponent.style.removeProperty('--relin-gallery-height');
      updateArrowState();
      return;
    }

    const activeSlide = viewerList.querySelector('[data-media-id].is-active');
    const height = activeSlide?.getBoundingClientRect().height || 0;
    if (height > 0) thumbnailComponent.style.setProperty('--relin-gallery-height', `${Math.round(height)}px`);
    updateArrowState();
  };

  const syncSelectedTarget = () => {
    const current = thumbnailStrip.querySelector('[data-target] button[aria-current="true"]')?.closest('[data-target]');
    if (current && !current.hidden) selectedTarget = current.dataset.target;
  };

  const setActiveTarget = (target, { updateGallery = true } = {}) => {
    const item = getItem(target);
    const slide = getSlide(target);
    if (!item || !slide || item.hidden || slide.hidden) return;

    selectedTarget = target;
    const activeSlide = viewerList.querySelector('[data-media-id].is-active');
    if (updateGallery && activeSlide !== slide) gallery.setActiveMedia?.(target, true);
    viewerList.querySelectorAll('[data-media-id]').forEach((entry) => entry.classList.toggle('is-active', entry === slide));
    thumbnailStrip.querySelectorAll('[data-target] button').forEach((button) => {
      if (button.closest('[data-target]')?.dataset.target === target) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    scrollSelectedIntoView(target);
  };

  const applyFixedThumbnailOrder = (items, group) => {
    const order = colourGroupOrder[group] || [];
    items.forEach((item) => {
      const position = item.dataset.relinMediaPosition;
      const index = order.indexOf(position);
      item.style.order = index === -1 ? '' : String(index);
    });
  };

  const applyModalFilter = () => {
    const currentModalContent = document.querySelector('product-modal[data-relin-universal-modal="true"] .product-media-modal__content');
    if (!currentModalContent || !activeColourGroup) return;
    currentModalContent.querySelectorAll('[data-media-id]').forEach((item) => {
      const group = item.dataset.relinColourGroup || mediaIdToGroup[item.dataset.mediaId] || '';
      if (group) item.dataset.relinColourGroup = group;
      const hidden = group !== activeColourGroup;
      item.hidden = hidden;
      item.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
  };

  const filterByColour = (colour) => {
    const nextGroup = colourGroups[colour] || '';
    if (!nextGroup) return;
    const groupChanged = activeColourGroup !== nextGroup;
    activeColourGroup = nextGroup;
    gallery.dataset.relinActiveColourGroup = activeColourGroup;

    const mediaItems = Array.from(viewerList.querySelectorAll('[data-media-id]'));
    const thumbnailItems = Array.from(thumbnailStrip.querySelectorAll('[data-target]'));
    const visibleMediaItems = mediaItems.filter((item) => item.dataset.relinColourGroup === activeColourGroup);
    const visibleThumbnailItems = thumbnailItems.filter((item) => item.dataset.relinColourGroup === activeColourGroup);

    if (!isDesktop()) {
      syncMobileSliderItems(activeColourGroup);
      const mobileTarget = getOrderedItems(viewerItemsByGroup.get(activeColourGroup) || [], activeColourGroup).find(
        (item) => item.dataset.relinMediaPosition === canonicalHeroPosition[activeColourGroup]
      )?.dataset.mediaId;
      if (mobileTarget) {
        selectedTarget = mobileTarget;
        gallery.setActiveMedia?.(mobileTarget, false);
      }
      applyModalFilter();
      syncRailHeight();
      return;
    }

    mediaItems.forEach((item) => {
      const hidden = item.dataset.relinColourGroup !== activeColourGroup;
      item.hidden = hidden;
      item.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
    thumbnailItems.forEach((item) => {
      const hidden = item.dataset.relinColourGroup !== activeColourGroup;
      item.hidden = hidden;
      item.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    });
    applyFixedThumbnailOrder(thumbnailItems, activeColourGroup);

    const selectedStillVisible = visibleThumbnailItems.some((item) => item.dataset.target === selectedTarget);
    const canonicalTarget = visibleThumbnailItems.find(
      (item) => item.dataset.relinMediaPosition === canonicalHeroPosition[activeColourGroup]
    )?.dataset.target;
    const nextTarget = groupChanged
      ? canonicalTarget || visibleThumbnailItems[0]?.dataset.target
      : selectedStillVisible
        ? selectedTarget
        : canonicalTarget || visibleThumbnailItems[0]?.dataset.target;
    if (nextTarget) setActiveTarget(nextTarget);

    applyModalFilter();
    syncRailHeight();
  };

  gallery.relinFilterByColour = filterByColour;

  viewer.addEventListener(
    'pointerdown',
    (event) => {
      lastPointerType = event.pointerType || '';
    },
    { capture: true, signal: listenerController.signal }
  );

  viewer.addEventListener(
    'click',
    (event) => {
      const toggle = event.target.closest?.('.product__media-toggle');
      const touchLike = event.pointerType === 'touch' || lastPointerType === 'touch' || !isFinePointer();
      lastPointerType = '';
      if (!toggle || !touchLike || mediaModal.hasAttribute('open')) return;

      const opener = toggle.closest('modal-opener');
      if (!opener) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      mediaModal.show(toggle);
    },
    { capture: true, signal: listenerController.signal }
  );

  thumbnailStrip.addEventListener('pointerover', (event) => {
    if (event.pointerType && !['mouse', 'pen'].includes(event.pointerType)) return;
    if (!isFinePointer()) return;
    const item = event.target.closest('[data-target]');
    if (!item || item.hidden || item.contains(event.relatedTarget)) return;
    setActiveTarget(item.dataset.target, { updateGallery: false });
  }, { signal: listenerController.signal });

  thumbnailStrip.addEventListener('click', (event) => {
    const item = event.target.closest('[data-target]');
    if (!item || item.hidden) return;

    schedule(() => {
      syncSelectedTarget();
      setActiveTarget(item.dataset.target);
    }, 0);
  }, { signal: listenerController.signal });

  thumbnailStrip.addEventListener('keydown', (event) => {
    const item = event.target.closest('[data-target]');
    if (!item || item.hidden || !['Enter', ' '].includes(event.key)) return;

    event.preventDefault();
    setActiveTarget(item.dataset.target);
  }, { signal: listenerController.signal });

  thumbnailComponent.addEventListener('click', (event) => {
    const button = event.target.closest('button[name="previous"], button[name="next"]');
    if (!button) return;

    event.preventDefault();
    const direction = button.name === 'next' ? 1 : -1;
    const distance = Math.max(120, (isDesktop() ? thumbnailStrip.clientHeight : thumbnailStrip.clientWidth) * 0.65);
    thumbnailStrip.scrollBy({
      top: isDesktop() ? direction * distance : 0,
      left: isDesktop() ? 0 : direction * distance,
      behavior: 'smooth',
    });
    schedule(updateArrowState, 180);
  }, { signal: listenerController.signal });

  thumbnailStrip.addEventListener('scroll', updateArrowState, { passive: true, signal: listenerController.signal });

  viewer.addEventListener('slideChanged', (event) => {
    const target = event.detail?.currentElement?.dataset?.mediaId;
    if (!target || getSlide(target)?.hidden) return;
    selectedTarget = target;
    scrollSelectedIntoView(target);
  }, { signal: listenerController.signal });

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('input[data-relin-colour-value]');
    if (input) schedule(() => filterByColour(input.value), 0);
  }, { signal: listenerController.signal });

  thumbnailObserver = new MutationObserver(() => {
    syncSelectedTarget();
    if (selectedTarget) scrollSelectedIntoView(selectedTarget);
    updateArrowState();
  });
  thumbnailObserver.observe(thumbnailStrip, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });

  const currentModalContent = document.querySelector('product-modal[data-relin-universal-modal="true"] .product-media-modal__content');
  if (currentModalContent) {
    modalObserver = new MutationObserver(applyModalFilter);
    modalObserver.observe(currentModalContent, { childList: true, subtree: true });
  }

  const syncResponsiveMode = () => {
    const nextDesktopMode = isDesktop();
    if (nextDesktopMode === lastDesktopMode) {
      syncRailHeight();
      return;
    }

    lastDesktopMode = nextDesktopMode;
    if (nextDesktopMode) restoreDesktopSliderItems();
    const currentColour = productInfo?.querySelector('input[data-relin-colour-value]:checked')?.value;
    if (currentColour) filterByColour(currentColour);
    syncRailHeight();
  };

  captureMediaItems();
  window.addEventListener('resize', syncResponsiveMode, { signal: listenerController.signal });
  schedule(() => {
    const initialColour = productInfo?.querySelector('input[data-relin-colour-value]:checked')?.value;
    if (initialColour) filterByColour(initialColour);
    syncRailHeight();
  }, 0);
  schedule(syncRailHeight, 400);
  updateArrowState();
})();

